(() => {
  const ROOT_SELECTOR = '[data-product-grid-content]';
  const MEDIA_GRID_SELECTOR = '[data-product-media-grid]';

  const initializedRoots = new WeakSet();
  const modalIndexes = new WeakMap();
  const modalItemCache = new WeakMap();
  const thumbnailCache = new WeakMap();
  let viewportFrame = 0;
  let pageBackdrop = null;

  const usesMobileDocumentLayer = () => window.matchMedia('(max-width: 749px)').matches;

  const ensurePageBackdrop = () => {
    if (pageBackdrop?.isConnected) return;
    pageBackdrop = document.createElement('div');
    pageBackdrop.className = 'sb-modal-page-backdrop sb-modal-page-backdrop--photo-media';
    pageBackdrop.setAttribute('aria-hidden', 'true');
    document.body.prepend(pageBackdrop);
  };

  const removePageBackdrop = () => {
    pageBackdrop?.remove();
    pageBackdrop = null;
  };

  const syncModalPresentation = (modal) => {
    const mobile = usesMobileDocumentLayer();
    if (modal.open && modal.matches(':modal') === !mobile) return;
    const focusedControl = modal.contains(document.activeElement) ? document.activeElement : null;
    if (modal.open) modal.close();
    if (mobile) {
      modal.setAttribute('aria-modal', 'true');
      modal.show();
      ensurePageBackdrop();
    } else {
      modal.removeAttribute('aria-modal');
      modal.showModal();
      removePageBackdrop();
    }
    focusedControl?.focus({ preventScroll: true });
  };

  const updateViewportState = () => {
    viewportFrame = 0;
    if (!document.documentElement.classList.contains('sb-media-gallery-modal-open')) return;

    const modal = getOpenModal();
    if (modal) syncModalPresentation(modal);

    const viewport = window.visualViewport;
    const height = Math.ceil(viewport?.height || window.innerHeight || document.documentElement.clientHeight || 0);
    const width = Math.ceil(viewport?.width || window.innerWidth || document.documentElement.clientWidth || 0);
    const top = Math.floor(viewport?.offsetTop || 0);
    const left = Math.floor(viewport?.offsetLeft || 0);

    document.documentElement.style.setProperty('--sb-media-gallery-modal-viewport-height', `${height}px`);
    document.documentElement.style.setProperty('--sb-media-gallery-modal-viewport-width', `${width}px`);
    document.documentElement.style.setProperty('--sb-media-gallery-modal-viewport-top', `${top}px`);
    document.documentElement.style.setProperty('--sb-media-gallery-modal-viewport-left', `${left}px`);
  };

  const requestViewportStateUpdate = () => {
    if (!document.documentElement.classList.contains('sb-media-gallery-modal-open')) return;
    if (viewportFrame) return;
    viewportFrame = window.requestAnimationFrame(updateViewportState);
  };

  const setOpenState = (isOpen) => {
    if (isOpen) {
      document.documentElement.classList.add('sb-media-gallery-modal-open');
      if (usesMobileDocumentLayer()) ensurePageBackdrop();
      updateViewportState();
      return;
    }

    if (viewportFrame) {
      window.cancelAnimationFrame(viewportFrame);
      viewportFrame = 0;
    }
    removePageBackdrop();
    document.documentElement.classList.toggle('sb-media-gallery-modal-open', isOpen);
  };

  const trapDocumentLayerFocus = (event, modal) => {
    if (event.key !== 'Tab' || !usesMobileDocumentLayer()) return;
    const controls = Array.from(modal.querySelectorAll('button:not([hidden]):not([disabled]), [href], [tabindex]:not([tabindex="-1"])'))
      .filter((control) => control instanceof HTMLElement && control.getClientRects().length > 0);
    if (!controls.length) {
      event.preventDefault();
      modal.focus({ preventScroll: true });
      return;
    }
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  };

  const getOpenModal = () => document.querySelector('.sb-product-main__media-gallery-modal[open]');

  const shouldAllowThumbnailScroll = (event) => {
    if (!(event.target instanceof Element)) return false;
    const thumbnails = event.target.closest('[data-media-gallery-modal-thumbnails]');
    if (!(thumbnails instanceof HTMLElement)) return false;
    if (thumbnails.scrollWidth <= thumbnails.clientWidth) return false;
    if (!('deltaX' in event)) return true;
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return false;
    return true;
  };

  const getMediaItems = (root) => {
    const mediaGrid = root.querySelector(MEDIA_GRID_SELECTOR);
    if (!mediaGrid) return [];
    return Array.from(mediaGrid.querySelectorAll('.sb-product-main__media-item'));
  };

  const getOpenLabel = (root) => {
    return root.dataset.mediaGalleryModalOpenLabel || '';
  };

  const ensureMediaTriggers = (root) => {
    const openLabel = getOpenLabel(root);
    getMediaItems(root).forEach((item) => {
      if (item.querySelector('[data-media-gallery-modal-trigger]')) return;
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'sb-product-main__media-modal-trigger';
      trigger.dataset.mediaGalleryModalTrigger = '';
      if (openLabel) trigger.setAttribute('aria-label', openLabel);
      item.appendChild(trigger);
    });
  };

  const prepareModalItem = (item) => {
    if (!(item instanceof HTMLElement)) return null;
    const clone = item.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return null;

    clone.querySelectorAll('[data-media-gallery-modal-trigger]').forEach((trigger) => trigger.remove());
    clone.querySelectorAll('img[srcset]').forEach((image) => {
      image.sizes = '(min-width: 750px) 600px, min(600px, calc(100vw - 72px))';
    });
    clone.querySelectorAll('video').forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;
      clone.classList.remove('is-video-ready');
      if (!video.getAttribute('src') && video.dataset.sbMediaSrc) video.src = video.dataset.sbMediaSrc;
      video.addEventListener('playing', () => clone.classList.add('is-video-ready'));
      video.addEventListener('error', () => clone.classList.remove('is-video-ready'));
      video.controls = false;
      video.removeAttribute('controls');
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
    });

    return clone;
  };

  const getThumbnailImageData = (item) => {
    if (!(item instanceof HTMLElement)) return null;

    const sourceImage =
      item.querySelector('[data-gallery-video-poster]') ||
      item.querySelector('img.sb-product-main__media-asset') ||
      item.querySelector('img');
    if (!(sourceImage instanceof HTMLImageElement)) return null;

    return {
      alt: sourceImage.getAttribute('alt') || '',
      src: sourceImage.currentSrc || sourceImage.getAttribute('src') || '',
    };
  };

  const getMediaItemSignature = (item) => {
    if (!(item instanceof HTMLElement)) return '';

    const video = item.querySelector('video.sb-product-main__media-asset--video');
    const isVideo = video instanceof HTMLVideoElement;
    const imageData = getThumbnailImageData(item);
    return JSON.stringify({
      type: isVideo ? 'video' : 'image',
      source: isVideo ? (video.getAttribute('src') || video.dataset.sbMediaSrc || video.currentSrc || video.querySelector('source[src]')?.getAttribute('src') || '') : (imageData?.src || ''),
      poster: isVideo ? (video.getAttribute('poster') || item.querySelector('[data-gallery-video-poster]')?.getAttribute('src') || '') : '',
      caption: item.querySelector('.sb-product-main__media-caption')?.textContent || '',
      alt: imageData?.alt || '',
    });
  };

  const getThumbnailLabel = (root, index) => {
    const modal = root.querySelector('[data-media-gallery-modal]');
    const template = modal?.dataset.mediaGalleryModalThumbnailLabel || '';
    return template.replace('__INDEX__', `${index + 1}`);
  };

  const updateActiveThumbnail = (root, activeIndex) => {
    const thumbnails = root.querySelector('[data-media-gallery-modal-thumbnails]');
    if (!(thumbnails instanceof HTMLElement)) return;

    let activeThumbnail = null;
    thumbnails.querySelectorAll('.sb-product-main__media-gallery-modal-thumbnail').forEach((thumbnail) => {
      const isActive = Number(thumbnail.dataset.mediaGalleryIndex) === activeIndex;
      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) activeThumbnail = thumbnail;
    });

    if (!(activeThumbnail instanceof HTMLElement)) return;
    window.requestAnimationFrame(() => {
      if (!activeThumbnail.classList.contains('is-active')) return;
      const thumbnailsRect = thumbnails.getBoundingClientRect();
      const activeThumbnailRect = activeThumbnail.getBoundingClientRect();
      const thumbnailsStyles = window.getComputedStyle(thumbnails);
      const scrollPaddingStart = Number.parseFloat(thumbnailsStyles.scrollPaddingInlineStart) || 0;
      const scrollPaddingEnd = Number.parseFloat(thumbnailsStyles.scrollPaddingInlineEnd) || 0;
      const visibleStart = thumbnailsRect.left + scrollPaddingStart;
      const visibleEnd = thumbnailsRect.right - scrollPaddingEnd;
      let scrollDelta = 0;

      if (activeThumbnailRect.left < visibleStart) {
        scrollDelta = activeThumbnailRect.left - visibleStart;
      } else if (activeThumbnailRect.right > visibleEnd) {
        scrollDelta = activeThumbnailRect.right - visibleEnd;
      }

      if (scrollDelta !== 0) thumbnails.scrollLeft += scrollDelta;
    });
  };

  const renderThumbnails = (root, activeIndex, items, signatures) => {
    const thumbnails = root.querySelector('[data-media-gallery-modal-thumbnails]');
    if (!(thumbnails instanceof HTMLElement)) return;

    const nextSignature = JSON.stringify(signatures);
    const cached = thumbnailCache.get(root);
    if (cached?.signature === nextSignature) {
      updateActiveThumbnail(root, activeIndex);
      return;
    }

    thumbnails.replaceChildren();
    items.forEach((item, index) => {
      const thumbnailImageData = getThumbnailImageData(item);
      if (!thumbnailImageData?.src) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sb-product-main__media-gallery-modal-thumbnail';
      button.dataset.mediaGalleryIndex = `${index}`;
      button.classList.toggle('is-active', index === activeIndex);
      button.setAttribute('aria-label', getThumbnailLabel(root, index));
      button.setAttribute('aria-pressed', index === activeIndex ? 'true' : 'false');

      const image = document.createElement('img');
      image.className = 'sb-product-main__media-gallery-modal-thumbnail-image';
      image.alt = thumbnailImageData.alt;
      image.decoding = 'async';
      image.loading = 'lazy';
      image.src = thumbnailImageData.src;
      button.appendChild(image);

      button.addEventListener('click', () => renderModalItem(root, index));
      thumbnails.appendChild(button);
    });
    thumbnailCache.set(root, { signature: nextSignature });
    updateActiveThumbnail(root, activeIndex);
  };

  const getCachedModalItems = (root, items, signatures) => {
    const nextSignature = JSON.stringify(signatures);
    const cached = modalItemCache.get(root);
    if (cached?.signature === nextSignature) return cached.items;

    const modalItems = items.map((item) => prepareModalItem(item));
    modalItemCache.set(root, {
      items: modalItems,
      signature: nextSignature,
    });
    return modalItems;
  };

  const renderModalItem = (root, index) => {
    const stage = root.querySelector('[data-media-gallery-modal-stage]');
    if (!(stage instanceof HTMLElement)) return;

    const items = getMediaItems(root);
    if (!items.length) return;

    const nextIndex = (index + items.length) % items.length;
    const signatures = items.map(getMediaItemSignature);
    const modalItems = getCachedModalItems(root, items, signatures);
    const modalItem = modalItems[nextIndex];
    if (!modalItem) return;

    modalIndexes.set(root, nextIndex);

    if (stage.dataset.mediaGalleryModalSignature !== JSON.stringify(signatures)) {
      stage.replaceChildren();
      stage.dataset.mediaGalleryModalSignature = JSON.stringify(signatures);
    }

    if (modalItem.parentElement !== stage) {
      stage.appendChild(modalItem);
    }

    Array.from(stage.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      child.hidden = child !== modalItem;
      if (child.hidden) child.querySelectorAll('video').forEach((video) => video.pause());
    });

    renderThumbnails(root, nextIndex, items, signatures);

    root.querySelector('[data-media-gallery-modal-previous]')?.toggleAttribute('hidden', items.length < 2);
    root.querySelector('[data-media-gallery-modal-next]')?.toggleAttribute('hidden', items.length < 2);

    modalItem.querySelectorAll('video').forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  };

  let lastModalTrigger = null;
  let lastPointerModalTrigger = null;

  const closeModal = (root) => {
    const modal = root.querySelector('[data-media-gallery-modal]');
    const stage = root.querySelector('[data-media-gallery-modal-stage]');
    if (!(modal instanceof HTMLDialogElement) || !modal.open) return;

    stage?.querySelectorAll('video').forEach((video) => video.pause());
    modal.close();
    if (lastPointerModalTrigger instanceof HTMLElement && document.activeElement === lastPointerModalTrigger) {
      lastPointerModalTrigger.blur();
    }
    // a click-drag on the close control or backdrop can leave an empty text
    // range selected behind the dialog, which paints as a full-width teal
    // strip (the theme ::selection color) once the modal is gone
    try {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) selection.removeAllRanges();
    } catch (_) {}
    setOpenState(false);
    // WebKit does not return focus to the opener after dialog.close() - it
    // lands on <main> instead; hand it back explicitly like Chrome does
    if (lastModalTrigger instanceof HTMLElement && document.contains(lastModalTrigger)) {
      lastModalTrigger.focus({ preventScroll: true });
    }
    lastModalTrigger = null;
    lastPointerModalTrigger = null;
    modal.removeAttribute('data-pointer-open');
    modal.removeAttribute('aria-modal');
  };

  const openModal = (root, trigger, { keyboardInitiated = false } = {}) => {
    lastModalTrigger = keyboardInitiated && trigger instanceof HTMLElement ? trigger : null;
    lastPointerModalTrigger = !keyboardInitiated && trigger instanceof HTMLElement ? trigger : null;
    const modal = root.querySelector('[data-media-gallery-modal]');
    const items = getMediaItems(root);
    const mediaItem = trigger.closest('.sb-product-main__media-item');
    const itemIndex = items.indexOf(mediaItem);
    if (!(modal instanceof HTMLDialogElement) || itemIndex < 0) return;

    renderModalItem(root, itemIndex);
    setOpenState(true);
    modal.toggleAttribute('data-pointer-open', !keyboardInitiated);
    syncModalPresentation(modal);
    const closeButton = modal.querySelector('[data-media-gallery-modal-close]');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus({ preventScroll: true });
    }
  };

  const bindTriggers = (root) => {
    ensureMediaTriggers(root);
    root.querySelectorAll('[data-media-gallery-modal-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      if (trigger.dataset.mediaGalleryModalTriggerBound === 'true') return;

      trigger.dataset.mediaGalleryModalTriggerBound = 'true';
      trigger.addEventListener('click', (event) => {
        openModal(root, trigger, { keyboardInitiated: event.detail === 0 });
      });
    });
  };

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || initializedRoots.has(root)) return;
    const modal = root.querySelector('[data-media-gallery-modal]');
    const mediaGrid = root.querySelector(MEDIA_GRID_SELECTOR);
    if (!(modal instanceof HTMLDialogElement) || !(mediaGrid instanceof HTMLElement)) return;

    initializedRoots.add(root);
    root.dataset.mediaGalleryModalOpenLabel =
      root.querySelector('[data-media-gallery-modal-trigger]')?.getAttribute('aria-label') || '';
    bindTriggers(root);

    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeModal(root);
    });

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal(root);
    });

    root.querySelector('[data-media-gallery-modal-close]')?.addEventListener('click', () => closeModal(root));
    root.querySelector('[data-media-gallery-modal-previous]')?.addEventListener('click', (event) => {
      event.preventDefault();
      renderModalItem(root, (modalIndexes.get(root) || 0) - 1);
    });
    root.querySelector('[data-media-gallery-modal-next]')?.addEventListener('click', (event) => {
      event.preventDefault();
      renderModalItem(root, (modalIndexes.get(root) || 0) + 1);
    });

    root.querySelectorAll('[data-media-gallery-modal-previous], [data-media-gallery-modal-next]').forEach((button) => {
      button.addEventListener('dblclick', (event) => {
        event.preventDefault();
      });
    });

    modal.addEventListener('keydown', (event) => {
      trapDocumentLayerFocus(event, modal);
      if (event.key === 'Tab') modal.removeAttribute('data-pointer-open');
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal(root);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        renderModalItem(root, (modalIndexes.get(root) || 0) - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        renderModalItem(root, (modalIndexes.get(root) || 0) + 1);
      }
    });

    const observer = new MutationObserver(() => bindTriggers(root));
    observer.observe(mediaGrid, { childList: true, subtree: true });
  };

  // Quick add replaces product roots. Document handlers belong to this module,
  // not to each root, so removed products can be garbage-collected.
  document.addEventListener('click', (event) => {
    const modal = getOpenModal();
    if (!modal || !(event.target instanceof Element)) return;
    if (event.target.closest('.sb-product-main__media-gallery-modal-surface, [data-media-gallery-modal-trigger]')) return;
    const root = modal.closest(ROOT_SELECTOR);
    if (!root) return;
    event.preventDefault();
    event.stopPropagation();
    closeModal(root);
  }, true);

  const blockBackgroundScroll = (event) => {
    if (!getOpenModal() || shouldAllowThumbnailScroll(event)) return;
    event.preventDefault();
  };
  document.addEventListener('wheel', blockBackgroundScroll, { capture: true, passive: false });
  document.addEventListener('touchmove', blockBackgroundScroll, { capture: true, passive: false });

  const init = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.unshift(scope);
    roots.forEach(initRoot);
  };

  window.SBProductMediaGalleryModal = window.SBProductMediaGalleryModal || {};
  window.SBProductMediaGalleryModal.init = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  window.addEventListener('resize', requestViewportStateUpdate, { passive: true });
  window.addEventListener('orientationchange', requestViewportStateUpdate);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', requestViewportStateUpdate, { passive: true });
    window.visualViewport.addEventListener('scroll', requestViewportStateUpdate, { passive: true });
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const modal = event.target.querySelector?.('.sb-product-main__media-gallery-modal[open]');
    const root = modal?.closest(ROOT_SELECTOR);
    if (root) closeModal(root);
  });
  document.addEventListener('close', (event) => {
    if (!(event.target instanceof HTMLDialogElement) || event.target.open) return;
    const modal = event.target.querySelector('.sb-product-main__media-gallery-modal[open]');
    const root = modal?.closest(ROOT_SELECTOR);
    if (root) closeModal(root);
  }, true);
})();
